import axios from ".";
import { Phrase } from "./phrasebook";

// Re-use existing types from phrasebook API
export interface EditorPhrase extends Phrase {
  original_language?: string;
  usage_count?: number;
}

export interface EditorBrand {
  id: string;
  name: string;
}

export interface PagedResult<T> {
  items: T[];
  total: number;
  page: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
}

/**
 * Find phrases with proper pagination for the editor interface
 */
export const findPhrasesEditor = async ({
  brandIds,
  page = 1,
  limit = 20,
  query,
  statusFilter,
  originalLanguageFilter,
  usedPhrasesOnly,
  suitableForTitle,
  suitableForDescription,
  suitableForBulletPoints,
  signal,
}: {
  brandIds?: string[];
  page?: number;
  limit?: number;
  query?: string;
  statusFilter?: string;
  originalLanguageFilter?: string;
  usedPhrasesOnly?: boolean;
  suitableForTitle?: boolean;
  suitableForDescription?: boolean;
  suitableForBulletPoints?: boolean;
  signal?: AbortSignal;
}) => {
  const params = new URLSearchParams();
  if (page) params.append("page", page.toString());
  if (limit) params.append("limit", limit.toString());
  if (query) params.append("query", query);
  if (statusFilter) params.append("status_filter", statusFilter);
  if (originalLanguageFilter) params.append("original_language_filter", originalLanguageFilter);
  if (usedPhrasesOnly) params.append("used_phrases_only", usedPhrasesOnly.toString());
  if (suitableForTitle !== undefined) params.append("suitable_for_title", suitableForTitle.toString());
  if (suitableForDescription !== undefined) params.append("suitable_for_description", suitableForDescription.toString());
  if (suitableForBulletPoints !== undefined) params.append("suitable_for_bullet_points", suitableForBulletPoints.toString());

  return axios.post<{data: PagedResult<EditorPhrase>}>(
    `/phrasebook-editor/phrases?${params.toString()}`,
    {
      brand_ids: brandIds,
    },
    {
      signal,
    }
  );
};

/**
 * Get all brands that have at least one associated global phrase for the editor
 */
export const getBrandsWithGlobalPhrasesEditor = async () => {
  return axios.get<{data: EditorBrand[]}>("/phrasebook-editor/brands");
};

/**
 * Get usage details for a specific phrase (all jobs using it)
 */
export const getPhraseUsage = async (phraseId: string) => {
  return axios.get<{data: Array<{
    job_id: string;
    master_id: string;
    created_at: string;
    status: string;
    phrase_type: string;
    phrase_order: number;
  }>}>(`/phrasebook-editor/phrases/${phraseId}/usage`);
};

export interface PhraseUpdateData {
  phrase?: string;
  status?: string;
  suitable_for_title?: boolean;
  suitable_for_description?: boolean;
  suitable_for_bullet_points?: boolean;
  brand_ids?: string[];
  translation_status?: Record<string, boolean>;
  original_language?: string;
}

/**
 * Update a phrase with new values
 */
export const updatePhrase = async (phraseId: string, updateData: PhraseUpdateData) => {
  return axios.put<{data: EditorPhrase}>(`/phrasebook-editor/phrases/${phraseId}`, updateData);
};

/**
 * Delete a phrase if it has no usage
 */
export const deletePhrase = async (phraseId: string) => {
  return axios.delete<{data: {deleted: boolean}}>(`/phrasebook-editor/phrases/${phraseId}`);
};

export interface PhraseCreateData {
  phrase: string;
  status?: string;
  suitable_for_title?: boolean;
  suitable_for_description?: boolean;
  suitable_for_bullet_points?: boolean;
  brand_ids?: string[];
  translation_status?: Record<string, boolean>;
  original_language: string;
}

/**
 * Check if a phrase already exists with the same text and original language
 */
export const checkDuplicatePhrase = async (phrase: string, originalLanguage: string) => {
  const params = new URLSearchParams();
  params.append("phrase", phrase);
  params.append("original_language", originalLanguage);
  
  return axios.get<{data: {exists: boolean, phrase: EditorPhrase | null}}>(
    `/phrasebook-editor/phrases/check-duplicate?${params.toString()}`
  );
};

/**
 * Create a new phrase
 */
export const createPhrase = async (createData: PhraseCreateData) => {
  return axios.post<{data: EditorPhrase}>("/phrasebook-editor/phrases/create", createData);
};